import React, { FC, useMemo } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { ResponseReportPrint } from '../../../../states/types/analyze'
import { format } from '../../../../utils/date'
import { setPath, setUrlParam } from '../../../../utils/url'
import ApplicationCardList from '../ApplicationCardList'
import BarChart from '../BarChart'
import CsvModule from '../CsvModule'
import LineChart from '../LineChart'
import TableList from '../TableList'
import { URL } from '../../../constants/url'
import { LangValue } from '../../../../states/types/common'

interface Props {
  date: string
  list: ResponseReportPrint
}

const Print: FC<Props> = (props) => {
  const {
    date,
    list,
  } = props

  const { language } = useParams<{ language: LangValue }>()
  const history = useHistory()

  const COLOR = "#F8BBBC"
  const STRONG_COLOR = "#F87E7F"

  const all_page_count = useMemo(() => list.tendency.all.reduce((count, value) => count + value.page_count, 0), [list.tendency.all])

  const clickApp = (name: string) => {
    history.push(`${setPath(setUrlParam(
      URL.APP_ANALYZE_DETAIL.path,
      [
        { key: 'name', value: name },
      ],
    ), language)}`)
  }

  return (
    <>
      {
        Number(format(date, "YYYYMM")) >= 202205 ? (
          <>
            <section className="page_contents content-area">
              <div className="content-block">
                <div className="block-detail">
                  <div className="grid_area">
                    <div className="grid_block block_4">
                      <div className="title">
                        印刷人数
                      </div>
                      <div className="value">
                        <strong
                          style={{
                            color: STRONG_COLOR,
                          }}
                        >
                          {list.summary.member_count}
                        </strong>
                        {`/${list.summary.max_member_count}人`}
                      </div>
                    </div>
                    <div className="grid_block block_4">
                      <div className="title">
                        印刷回数
                      </div>
                      <div className="value">
                        <strong
                          style={{
                            color: STRONG_COLOR,
                          }}
                        >
                          {list.summary.execute_count.toLocaleString()}
                        </strong>
                        回
                      </div>
                    </div>
                    <div
                      className="grid_block block_4 reverse"
                      style={{
                        backgroundColor: STRONG_COLOR,
                      }}
                    >
                      <div className="title">
                        印刷枚数
                      </div>
                      <div className="value">
                        <strong>
                          {list.summary.page_count.toLocaleString()}
                        </strong>
                        枚
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <section className="page_contents content-area">
              <div className="content-block">
                <div className="block-header">
                  <div className="title">
                    印刷の傾向
                  </div>
                </div>
                <div className="block-detail">
                  {
                    all_page_count ? (
                      <div className="grid_area">
                        <div className="grid_block grid_title">
                          期間別
                        </div>
                        <div className="grid_block block_6">
                          <div className="title center">
                            回数
                          </div>
                          <LineChart
                            list={
                              list.tendency.all.map((value) => (
                                {
                                  label: value.label,
                                  value: value.execute_count,
                                }
                              ))
                            }
                            chartSize={200}
                            color={COLOR}
                            strongColor={STRONG_COLOR}
                            type="times"
                            scale
                          />
                        </div>
                        <div className="grid_block block_6">
                          <div className="title center">
                            枚数
                          </div>
                          <LineChart
                            list={
                              list.tendency.all.map((value) => (
                                {
                                  label: value.label,
                                  value: value.page_count,
                                }
                              ))
                            }
                            chartSize={200}
                            color={COLOR}
                            strongColor={STRONG_COLOR}
                            type="page"
                            scale
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="no_data_area">
                        <div className="no_data_text">
                          表示する印刷データはありません
                        </div>
                      </div>
                    )
                  }
                  {
                    list.summary.member_count ? (
                      <div className="grid_area">
                        <div className="grid_block grid_title">
                          曜日別
                        </div>
                        <div className="grid_block block_6">
                          <div className="title center">
                            回数
                          </div>
                          <BarChart
                            list={
                              list.tendency.weekdays.map((value) => (
                                {
                                  label: value.label,
                                  value: value.execute_count,
                                }
                              ))
                            }
                            chartSize={200}
                            color={COLOR}
                            strongColor={STRONG_COLOR}
                            type="times"
                            scale
                          />
                        </div>
                        <div className="grid_block block_6">
                          <div className="title center">
                            枚数
                          </div>
                          <BarChart
                            list={
                              list.tendency.weekdays.map((value) => (
                                {
                                  label: value.label,
                                  value: value.page_count,
                                }
                              ))
                            }
                            chartSize={200}
                            color={COLOR}
                            strongColor={STRONG_COLOR}
                            type="page"
                            scale
                          />
                        </div>
                      </div>
                    ) : (
                      <></>
                    )
                  }
                  {
                    list.summary.member_count ? (
                      <div className="grid_area">
                        <div className="grid_block grid_title">
                          時間帯別
                        </div>
                        <div className="grid_block block_6">
                          <div className="title center">
                            回数
                          </div>
                          <BarChart
                            list={
                              list.tendency.times.map((value) => (
                                {
                                  label: value.label,
                                  value: value.execute_count,
                                }
                              ))
                            }
                            chartSize={200}
                            color={COLOR}
                            strongColor={STRONG_COLOR}
                            ruler
                            labelSkip={2}
                            type="times"
                            scale
                          />
                        </div>
                        <div className="grid_block block_6">
                          <div className="title center">
                            枚数
                          </div>
                          <BarChart
                            list={
                              list.tendency.times.map((value) => (
                                {
                                  label: value.label,
                                  value: value.page_count,
                                }
                              ))
                            }
                            chartSize={200}
                            color={COLOR}
                            strongColor={STRONG_COLOR}
                            ruler
                            labelSkip={2}
                            type="page"
                            scale
                          />
                        </div>
                      </div>
                    ) : (
                      <></>
                    )
                  }
                </div>
              </div>
            </section>
            <section className="page_contents content-area">
              <div className="content-block">
                <div className="block-header">
                  <div className="title">
                    【電子帳簿保存法】対応状況
                  </div>
                </div>
                <div className="block-detail">
                  {
                    list.electronic_ledger.supported_services.length ? (
                      <ApplicationCardList
                        clickAppCallback={clickApp}
                        list={list.electronic_ledger.supported_services || []}
                      />
                    ) : (
                      <div className="no_data_area">
                        <div className="no_data_text">
                          電子帳簿保存法に対応できるアプリケーションがありません
                        </div>
                      </div>
                    )
                  }
                </div>
              </div>
            </section>
            <section className="page_contents content-area">
              <div className="content-block">
                <div className="block-header">
                  <div className="title">
                    【電子帳簿保存法】未対応の可能性があるファイル
                  </div>
                  {
                    list.electronic_ledger.unsupported_files.length ? (
                      <CsvModule
                        title={
                          `【電子帳簿保存法】未対応の可能性があるファイル ${format(date, "YYYY年MM月")}`
                        }
                        labels={{
                          name: "ファイル名",
                          team: "チーム",
                          times: "印刷回数",
                          pages: "印刷枚数",
                        }}
                        list={
                          list.electronic_ledger.unsupported_files.map((value) => (
                            {
                              name: value.name,
                              team: value.team_name,
                              times: value.execute_count,
                              pages: value.page_count,
                            }
                          ))
                        }
                      />
                    ) : (
                      <></>
                    )
                  }
                </div>
                <div className="block-detail">
                  {
                    list.electronic_ledger.unsupported_files.length ? (
                      <div className="grid_area">
                        <div className="grid_block no_border">
                          <TableList
                            labels={{
                              name: "ファイル名",
                              team: "チーム名",
                              times: "印刷回数",
                              pages: "印刷枚数",
                            }}
                            list={
                              list.electronic_ledger.unsupported_files.map((value) => (
                                {
                                  name: value.name,
                                  team: value.team_name,
                                  times: value.execute_count,
                                  pages: value.page_count,
                                }
                              ))
                            }
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="no_data_area">
                        <div className="no_data_text">
                          未対応の可能性があるファイルはありません
                        </div>
                      </div>
                    )
                  }
                </div>
              </div>
            </section>
            <section className="page_contents content-area">
              <div className="content-block">
                <div className="block-header">
                  <div className="title">
                    ファイル別印刷状況
                  </div>
                  {
                    list.frequency.files.length ? (
                      <CsvModule
                        title={
                          `ファイル別印刷状況 ${format(date, "YYYY年MM月")}`
                        }
                        labels={{
                          name: "ファイル名",
                          times: "印刷回数",
                          pages: "印刷枚数",
                        }}
                        list={
                          list.frequency.files.map((value) => (
                            {
                              name: value.name,
                              times: value.execute_count,
                              pages: value.page_count,
                            }
                          ))
                        }
                      />
                    ) : (
                      <></>
                    )
                  }
                </div>
                <div className="block-detail">
                  {
                    list.frequency.files.length ? (
                      <div className="grid_area">
                        <div className="grid_block no_border">
                          <TableList
                            labels={{
                              name: "ファイル名",
                              times: "印刷回数",
                              pages: "印刷枚数",
                            }}
                            list={
                              list.frequency.files.map((value) => (
                                {
                                  name: value.name,
                                  times: value.execute_count,
                                  pages: value.page_count,
                                }
                              ))
                            }
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="no_data_area">
                        <div className="no_data_text">
                          印刷されたファイルがありません
                        </div>
                      </div>
                    )
                  }
                </div>
              </div>
            </section>
            <section className="page_contents content-area">
              <div className="content-block">
                <div className="block-header">
                  <div className="title">
                    チーム別印刷状況
                  </div>
                  {
                    list.frequency.teams.length ? (
                      <CsvModule
                        title={
                          `チーム別印刷状況 ${format(date, "YYYY年MM月")}`
                        }
                        labels={{
                          name: "チーム",
                          use_count: "印刷人数",
                          times: "印刷回数",
                          pages: "印刷枚数",
                          most: "最も印刷したファイル",
                        }}
                        list={
                          list.frequency.teams.map((value) => (
                            {
                              name: value.name,
                              use_count: value.use_count,
                              times: value.execute_count,
                              pages: value.page_count,
                              most: value.most_print_file_name,
                            }
                          ))
                        }
                      />
                    ) : (
                      <></>
                    )
                  }
                </div>
                <div className="block-detail">
                  {
                    list.frequency.files.length ? (
                      <div className="grid_area">
                        <div className="grid_block no_border">
                          <TableList
                            labels={{
                              name: "チーム名",
                              use_count: "印刷人数",
                              times: "印刷回数",
                              pages: "印刷枚数",
                              most: "最も印刷したファイル",
                            }}
                            list={
                              list.frequency.teams.map((value) => (
                                {
                                  name: value.name,
                                  use_count: value.use_count,
                                  times: value.execute_count,
                                  pages: value.page_count,
                                  most: value.most_print_file_name,
                                }
                              ))
                            }
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="no_data_area">
                        <div className="no_data_text">
                          印刷されたファイルがありません
                        </div>
                      </div>
                    )
                  }
                </div>
              </div>
            </section>
            <section className="page_contents content-area">
              <div className="content-block">
                <div className="block-header">
                  <div className="title">
                    ユーザー別印刷状況
                  </div>
                  {
                    list.frequency.members.length ? (
                      <CsvModule
                        title={
                          `ユーザー別印刷状況 ${format(date, "YYYY年MM月")}`
                        }
                        labels={{
                          name: "ユーザー",
                          times: "印刷回数",
                          pages: "印刷枚数",
                          most: "最も印刷したファイル",
                        }}
                        list={
                          list.frequency.members.map((value) => (
                            {
                              name: value.name,
                              times: value.execute_count,
                              pages: value.page_count,
                              most: value.most_print_file_name,
                            }
                          ))
                        }
                      />
                    ) : (
                      <></>
                    )
                  }
                </div>
                <div className="block-detail">
                  {
                    list.frequency.members.length ? (
                      <div className="grid_area">
                        <div className="grid_block no_border">
                          <TableList
                            labels={{
                              name: "氏名",
                              times: "印刷回数",
                              pages: "印刷枚数",
                              most: "最も印刷したファイル",
                            }}
                            list={
                              list.frequency.members.map((value) => (
                                {
                                  name: value.name,
                                  times: value.execute_count,
                                  pages: value.page_count,
                                  most: value.most_print_file_name,
                                }
                              ))
                            }
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="no_data_area">
                        <div className="no_data_text">
                          印刷されたファイルがありません
                        </div>
                      </div>
                    )
                  }
                </div>
              </div>
            </section>
          </>
        ) : (
          <div className="no_data_area">
            <div className="no_data_title">
              レポートを閲覧できない期間が指定されています。
            </div>
            <div className="no_data_text">
              期間条件をご確認ください。
            </div>
          </div>
        )
      }
    </>
  )
}

export default Print
